import type { BadgeVariant } from "@fox1ck-org/ui";
import { t } from "@/lib/i18n";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { createClient } from "@/lib/connect";
import { fetchAll } from "@/lib/services/catalog";
import { RuleService } from "@gen/tracker/v1/rule_connect";
import {
  type Action,
  ListRuleEventsFilter,
  ListRulesFilter,
  Rule,
  RuleDomain,
  RuleEvent,
  Severity,
} from "@gen/tracker/v1/rule_pb";
import { type FieldError as RuleFieldError, Pagination } from "@gen/tracker/v1/common_pb";

const client = createClient(RuleService);

export const ruleKeys = {
  all: ["rules"] as const,
  list: (params: ListRulesParams) => [...ruleKeys.all, "list", params] as const,
  detail: (id: string) => [...ruleKeys.all, "detail", id] as const,
  events: (params: ListRuleEventsParams) => [...ruleKeys.all, "events", params] as const,
  eventsRoot: () => [...ruleKeys.all, "events"] as const,
};

// ---------------------------------------------------------------------------
// Rules — list / get
// ---------------------------------------------------------------------------

export interface ListRulesParams {
  search?: string;
  domain?: RuleDomain;
  enabled?: boolean;
}

/** Every rule under the filter (a workspace keeps tens), page after page. */
export function useListRules(
  params: ListRulesParams = {},
  options?: Partial<UseQueryOptions<{ items: Rule[]; total: number }>>,
) {
  return useQuery({
    queryKey: ruleKeys.list(params),
    placeholderData: keepPreviousData,
    queryFn: () =>
      fetchAll(async (offset, limit) => {
        const res = await client.listRules({
          filter: new ListRulesFilter({
            search: params.search || undefined,
            domain: params.domain,
            enabled: params.enabled,
          }),
          pagination: new Pagination({ limit, offset }),
        });
        return { items: res.items, total: res.pagination?.total ?? res.items.length };
      }),
    ...options,
  });
}

export interface RuleWithRefs {
  rule: Rule | undefined;
  referencedBy: string[];
}

export function useGetRule(
  id: string | undefined,
  options?: Partial<UseQueryOptions<RuleWithRefs>>,
) {
  return useQuery({
    queryKey: ruleKeys.detail(id ?? ""),
    enabled: !!id,
    queryFn: async () => {
      if (!id) return { rule: undefined, referencedBy: [] } satisfies RuleWithRefs;
      const res = await client.getRule({ id });
      return { rule: res.rule, referencedBy: res.referencedBy } satisfies RuleWithRefs;
    },
    ...options,
  });
}

// ---------------------------------------------------------------------------
// Rules — mutations
// ---------------------------------------------------------------------------

export function useCreateRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rule: Rule) => {
      const res = await client.createRule({ rule });
      return res.rule!;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ruleKeys.all });
    },
  });
}

export function useUpdateRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rule: Rule) => {
      const res = await client.updateRule({ rule });
      return res.rule!;
    },
    onSuccess: (rule) => {
      void qc.invalidateQueries({ queryKey: ruleKeys.all });
      qc.setQueryData<RuleWithRefs>(ruleKeys.detail(rule.id), (prev) => ({
        rule,
        referencedBy: prev?.referencedBy ?? [],
      }));
    },
  });
}

export function useDeleteRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await client.deleteRule({ id });
      return id;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ruleKeys.all });
    },
  });
}

// useValidateRule runs the BE ValidateRule RPC against the in-flight draft for
// live inline error painting in the editor. Modeled on useValidateFlow: the
// caller paces calls via `enabled` (debounced from the editor). The query key
// hashes the rule's JSON so identical drafts dedupe.
export function useValidateRule(
  rule: Rule | null,
  enabled: boolean,
): { errors: RuleFieldError[]; isLoading: boolean; refetch: () => void } {
  const query = useQuery({
    queryKey: ["rule-validate", rule ? rule.toJsonString() : null],
    enabled: enabled && !!rule,
    queryFn: async () => {
      if (!rule) return [] as RuleFieldError[];
      const res = await client.validateRule({ rule });
      return res.errors;
    },
    staleTime: 1000,
  });
  return {
    errors: query.data ?? [],
    isLoading: query.isFetching,
    refetch: () => void query.refetch(),
  };
}

// ---------------------------------------------------------------------------
// Rule events (METRICS firings)
// ---------------------------------------------------------------------------

export interface ListRuleEventsParams {
  unacknowledgedOnly?: boolean;
  ruleId?: string;
  severity?: Severity;
  limit?: number;
  offset?: number;
}

export function useListRuleEvents(
  params: ListRuleEventsParams = {},
  options?: Partial<UseQueryOptions<{ events: RuleEvent[]; total: number }>>,
) {
  return useQuery({
    queryKey: ruleKeys.events(params),
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const res = await client.listRuleEvents({
        filter: new ListRuleEventsFilter({
          unacknowledgedOnly: params.unacknowledgedOnly,
          ruleId: params.ruleId,
          severity: params.severity,
        }),
        pagination: new Pagination({
          limit: params.limit ?? 50,
          offset: params.offset ?? 0,
        }),
      });
      return { events: res.events, total: res.pagination?.total ?? res.events.length };
    },
    ...options,
  });
}

// useAcknowledgeRuleEvent — optimistic acknowledge mirroring the old alert
// flow: every cached events query gets the event flipped to acknowledged (and
// dropped from unacknowledged-only views) immediately; rollback on error.
export function useAcknowledgeRuleEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (eventId: string) => {
      const res = await client.acknowledgeRuleEvent({ eventId });
      return res.event!;
    },
    onMutate: async (eventId) => {
      await qc.cancelQueries({ queryKey: ruleKeys.eventsRoot() });
      const snapshots = qc.getQueriesData<{ events: RuleEvent[]; total: number }>({
        queryKey: ruleKeys.eventsRoot(),
      });
      for (const [key, data] of snapshots) {
        if (!data) continue;
        const params = key[2] as ListRuleEventsParams | undefined;
        const hit = data.events.some((e) => e.id === eventId);
        if (!hit) continue;
        if (params?.unacknowledgedOnly) {
          qc.setQueryData(key, {
            ...data,
            events: data.events.filter((e) => e.id !== eventId),
            total: Math.max(0, data.total - 1),
          });
        } else {
          qc.setQueryData(key, {
            ...data,
            events: data.events.map((e) =>
              e.id === eventId ? new RuleEvent({ ...e, acknowledged: true }) : e,
            ),
          });
        }
      }
      return { snapshots };
    },
    onError: (_err, _id, ctx) => {
      if (!ctx?.snapshots) return;
      for (const [key, data] of ctx.snapshots) {
        qc.setQueryData(key, data);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ruleKeys.eventsRoot() });
    },
  });
}

// ---------------------------------------------------------------------------
// AI composer
// ---------------------------------------------------------------------------

// useSuggestRule turns a plain-English description into a draft Rule (no id)
// the editor can open pre-filled. The `rationale` explains the model's choice;
// `errors` carry any residual validation issues the model couldn't resolve.
export function useSuggestRule() {
  return useMutation({
    mutationFn: async (input: {
      description: string;
      domain: RuleDomain;
    }): Promise<{ rule: Rule | undefined; rationale: string; errors: RuleFieldError[] }> => {
      const res = await client.suggestRule({
        description: input.description,
        domain: input.domain,
      });
      return { rule: res.rule, rationale: res.rationale, errors: res.errors };
    },
  });
}

// ---------------------------------------------------------------------------
// Pure presentation helpers
// ---------------------------------------------------------------------------

export function ruleDomainLabel(d: RuleDomain): string {
  switch (d) {
    case RuleDomain.CLICK:
      return t("rules.domain.click");
    case RuleDomain.METRICS:
      return t("rules.domain.metrics");
    default:
      return "—";
  }
}

/** The two badge tones: routing uses the accent, alert uses warning. */
export function ruleDomainBadgeVariant(d: RuleDomain): BadgeVariant {
  switch (d) {
    case RuleDomain.CLICK:
      return "info";
    case RuleDomain.METRICS:
      return "warn";
    default:
      return "neutral";
  }
}

export function severityLabel(s: Severity): string {
  switch (s) {
    case Severity.INFO:
      return t("rules.sev.info");
    case Severity.WARNING:
      return t("rules.sev.warning");
    case Severity.CRITICAL:
      return t("rules.sev.critical");
    default:
      return "—";
  }
}

export function severityTone(s: Severity): string {
  switch (s) {
    case Severity.INFO:
      return "bg-info/15 text-info";
    case Severity.WARNING:
      return "bg-warn/15 text-warn";
    case Severity.CRITICAL:
      return "bg-destructive/15 text-destructive";
    default:
      return "bg-muted text-muted-foreground";
  }
}

/** Compact relative formatter shared by the table + events views. */
export function relativeTimeFrom(date: Date | undefined, now: Date = new Date()): string {
  if (!date) return "—";
  const ms = now.getTime() - date.getTime();
  if (!Number.isFinite(ms)) return "—";
  if (ms < 60_000) return t("rules.ago.now");
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return t("rules.ago.m", { n: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t("rules.ago.h", { n: hours });
  const days = Math.floor(hours / 24);
  if (days < 30) return t("rules.ago.d", { n: days });
  const months = Math.floor(days / 30);
  return t("rules.ago.mo", { n: months });
}

/**
 * ruleActionSummary — short human label for a rule's action list, e.g.
 * "Route → offer" / "Block" / "Notify (2)".
 */
export function ruleActionSummary(actions: Action[]): string {
  if (actions.length === 0) return t("rules.act.none");
  const parts: string[] = [];
  for (const a of actions) {
    switch (a.kind.case) {
      case "route": {
        const tgt = a.kind.value.offerId
          ? t("rules.act.route.offer")
          : a.kind.value.landerId
            ? t("rules.act.route.lander")
            : t("rules.act.route.default");
        parts.push(t("rules.act.route", { target: tgt }));
        break;
      }
      case "block":
        parts.push(t("rules.act.block"));
        break;
      case "notify":
        parts.push(t("rules.act.notify", { n: a.kind.value.destinationIds.length }));
        break;
      default:
        break;
    }
  }
  return parts.join(" · ") || t("rules.act.none");
}

/**
 * ruleEventSummary — "observed vs threshold" line from the event payload when
 * present, otherwise the payload `message`, otherwise null.
 */
export function ruleEventSummary(payloadJson: string): string | null {
  if (!payloadJson || !payloadJson.trim()) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(payloadJson);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
  const p = parsed as Record<string, unknown>;
  const observed = scalarString(p.observed);
  const threshold = scalarString(p.threshold);
  if (observed !== null && threshold !== null) {
    return t("rules.event.observed", { observed, threshold });
  }
  return scalarString(p.message);
}

function scalarString(v: unknown): string | null {
  if (typeof v === "string" && v) return v;
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  return null;
}
